import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Users, Globe, Lock, Shield, Camera, Image as ImageIcon, ChevronLeft, Save, CheckCircle2, Plus, X, Key, Loader2 } from 'lucide-react';
import { cn, getCommunityPath } from '../utils';
import { useActivities } from '../context/ActivitiesContext';
import { api } from '../api';

const CommunitySettings = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { updateCommunity } = useActivities();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const [name, setName] = useState('');
  const [tagline, setTagline] = useState('');
  const [description, setDescription] = useState('');
  const [privacy, setPrivacy] = useState('public');
  const [autoApprove, setAutoApprove] = useState(true);
  const [joinPassword, setJoinPassword] = useState('');
  const [rules, setRules] = useState([]);
  const [newRule, setNewRule] = useState('');
  const [coverImage, setCoverImage] = useState(null);
  const [communityIcon, setCommunityIcon] = useState(null);
  
  const coverInputRef = React.useRef(null);
  const iconInputRef = React.useRef(null);
  const successTimeoutRef = React.useRef(null);

  useEffect(() => {
    return () => {
      if (successTimeoutRef.current) clearTimeout(successTimeoutRef.current);
    };
  }, []);

  useEffect(() => {
    const fetchCommunity = async () => {
      try {
        setLoading(true);
        const data = await api.get(`/communities/${id}`);
        setName(data.name || '');
        setTagline(data.tagline || '');
        setDescription(data.description || '');
        setPrivacy(data.privacy || 'public');
        setRules(data.rules || []);
        setCoverImage(data.coverImage || null);
        setCommunityIcon(data.icon || null);
        
        if (data.settings) {
          setAutoApprove(data.settings.auto_approve !== false);
          setJoinPassword(data.settings.join_password || '');
        }
      } catch (err) {
        console.error('Failed to fetch community', err);
        setError('Failed to load community settings.');
      } finally {
        setLoading(false);
      }
    };
    fetchCommunity();
  }, [id]);

  const handleFileChange = (e, setter) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => { setter(reader.result); };
      reader.readAsDataURL(file);
    }
  };

  const addRule = () => {
    if (newRule.trim()) {
      setRules([...rules, newRule.trim()]);
      setNewRule('');
    }
  };

  const removeRule = (index) => {
    setRules(rules.filter((_, i) => i !== index));
  };

  const handleSave = async () => {
    if (!name.trim()) {
      setError('Please enter a community name');
      return;
    }

    setSaving(true);
    setError('');
    setSuccess(false);

    try {
      const updatedCommunity = await updateCommunity(id, {
        name,
        tagline,
        description,
        privacy,
        settings: {
          auto_approve: privacy === 'private' ? autoApprove : true,
          join_password: privacy === 'private' ? joinPassword : null
        },
        rules,
        coverImage,
        icon: communityIcon
      });
      setSuccess(true);
      successTimeoutRef.current = setTimeout(() => setSuccess(false), 3000);
    } catch (err) {
      setError('Failed to update community. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return (
    <div className="flex flex-col items-center justify-center min-h-[60vh] text-gray-400">
      <Loader2 size={40} className="animate-spin mb-4 text-brand-indigo" />
      <p className="font-bold uppercase tracking-widest text-xs">Loading Settings...</p>
    </div>
  );

  return (
    <div className="max-w-4xl mx-auto px-4 py-6 md:py-8">
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center gap-4">
          <button 
            onClick={() => navigate(`/c/${id}`)}
            className="p-3 hover:bg-gray-100 rounded-2xl transition-all text-gray-400 hover:text-gray-900"
          >
            <ChevronLeft size={24} />
          </button>
          <div>
            <h1 className="text-3xl font-bold text-gray-900 tracking-tight">Hub Management</h1>
            <p className="text-gray-500 font-medium text-sm">Configure your community settings</p>
          </div>
        </div>
        
        <button 
          onClick={handleSave}
          disabled={saving}
          className="px-6 py-3 bg-brand-indigo text-white font-bold rounded-2xl shadow-xl shadow-brand-indigo/20 flex items-center gap-2 hover:bg-brand-indigo/90 transition-all disabled:opacity-50"
        >
          {saving ? <Loader2 size={18} className="animate-spin" /> : <Save size={18} />}
          Save Changes
        </button>
      </div>

      <div className="space-y-8">
        {error && (
          <div className="p-4 bg-red-50 border border-red-100 text-red-600 rounded-2xl text-[10px] font-bold uppercase tracking-[0.15em] flex items-center gap-2">
            <Shield size={16} />
            {error}
          </div>
        )}

        {success && (
          <div className="p-4 bg-emerald-50 border border-emerald-100 text-emerald-600 rounded-2xl text-[10px] font-bold uppercase tracking-[0.15em] flex items-center gap-2">
            <CheckCircle2 size={16} />
            Settings updated successfully!
          </div>
        )}

        <div className="bg-white rounded-[2.5rem] border border-gray-100 overflow-hidden shadow-xl shadow-gray-100/50">
          <input 
            type="file" 
            ref={coverInputRef} 
            onChange={(e) => handleFileChange(e, setCoverImage)} 
            accept="image/*" 
            className="hidden" 
          />
          <input 
            type="file" 
            ref={iconInputRef} 
            onChange={(e) => handleFileChange(e, setCommunityIcon)} 
            accept="image/*" 
            className="hidden" 
          />
          <div className="h-48 bg-brand-indigo relative overflow-hidden">
             {coverImage && <img src={coverImage} alt="Cover" className="w-full h-full object-cover" />}
             <div className="absolute inset-0 bg-black/20 group hover:bg-black/30 transition-all flex items-center justify-center">
                <button 
                  onClick={() => coverInputRef.current?.click()}
                  className="p-4 bg-white/20 hover:bg-white/30 text-white rounded-2xl backdrop-blur-md transition-all flex items-center gap-2 font-bold text-[10px] uppercase tracking-[0.15em]"
                >
                    <Camera size={20} />
                    Update Cover
                </button>
             </div>
          </div>
          <div className="px-8 pb-8 -mt-12 relative z-10">
            <div className="flex items-end gap-6 mb-8">
              <div className="w-32 h-32 rounded-3xl bg-white p-2 shadow-2xl shadow-gray-200">
                 <div 
                  onClick={() => iconInputRef.current?.click()}
                  className="w-full h-full rounded-2xl bg-gray-50 flex items-center justify-center text-gray-300 relative group cursor-pointer hover:bg-gray-100 transition-all overflow-hidden"
                 >
                    {communityIcon ? (
                      <img src={communityIcon} alt="Icon" className="w-full h-full object-cover" />
                    ) : (
                      <ImageIcon size={40} />
                    )}
                    <div className="absolute inset-0 flex items-center justify-center bg-black/40 text-white opacity-0 group-hover:opacity-100 transition-opacity">
                       <Camera size={24} />
                    </div>
                 </div>
              </div>
              <div className="flex-1 pb-2">
                <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-[0.15em] mb-1.5">Community Name</label>
                <input 
                  type="text" 
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="w-full px-0 py-2 border-b-2 border-gray-100 focus:border-brand-indigo bg-transparent font-bold text-3xl transition-all outline-none tracking-tight"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="space-y-6">
                <div>
                  <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-[0.15em] mb-2">One-line Tagline</label>
                  <input 
                    type="text" 
                    value={tagline}
                    onChange={(e) => setTagline(e.target.value)}
                    placeholder="Briefly describe the community's purpose"
                    className="w-full px-6 py-4 bg-gray-50 border-none rounded-2xl focus:ring-2 focus:ring-brand-indigo/20 font-bold text-sm transition-all"
                  />
                </div>
                <div>
                  <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-[0.15em] mb-2">About / Description</label>
                  <textarea 
                    rows="5"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder="Tell potential members what they can expect..."
                    className="w-full px-6 py-4 bg-gray-50 border-none rounded-2xl focus:ring-2 focus:ring-brand-indigo/20 font-medium text-sm transition-all resize-none leading-relaxed"
                  ></textarea>
                </div>
              </div>

              <div className="space-y-6">
                <section>
                   <h2 className="text-[10px] font-bold text-gray-400 uppercase tracking-[0.15em] mb-3">Who can join?</h2>
                   <div className="grid grid-cols-2 gap-3">
                      <button 
                        onClick={() => setPrivacy('public')}
                        className={cn(
                          "flex items-center gap-3 p-4 rounded-3xl border-2 transition-all text-left",
                          privacy === 'public' ? "border-brand-indigo bg-brand-indigo/5" : "border-gray-50 bg-white hover:border-gray-200"
                        )}
                      >
                        <Globe size={18} className={privacy === 'public' ? "text-brand-indigo" : "text-gray-400"} />
                        <span className="font-bold text-gray-900 text-sm tracking-tight">Public</span>
                      </button>
                      <button 
                        onClick={() => setPrivacy('private')}
                        className={cn(
                          "flex items-center gap-3 p-4 rounded-3xl border-2 transition-all text-left",
                          privacy === 'private' ? "border-brand-indigo bg-brand-indigo/5" : "border-gray-50 bg-white hover:border-gray-200"
                        )}
                      >
                        <Lock size={18} className={privacy === 'private' ? "text-brand-indigo" : "text-gray-400"} />
                        <span className="font-bold text-gray-900 text-sm tracking-tight">Private</span>
                      </button>
                   </div>

                   {privacy === 'private' && (
                     <motion.div
                       initial={{ opacity: 0, height: 0 }}
                       animate={{ opacity: 1, height: 'auto' }}
                       className="mt-4 p-5 bg-gray-50 rounded-3xl space-y-4"
                     >
                       <button 
                         onClick={() => setAutoApprove(!autoApprove)}
                         className="w-full flex items-center justify-between"
                       >
                         <div className="text-left">
                           <p className="font-bold text-gray-900 text-sm tracking-tight">Auto-Approve</p>
                           <p className="text-[10px] text-gray-500 font-bold uppercase tracking-[0.15em]">Instantly approve requests</p>
                         </div>
                         <div className={cn("w-10 h-6 rounded-full relative transition-colors", autoApprove ? "bg-brand-indigo" : "bg-gray-300")}>
                           <div className={cn("absolute top-1 w-4 h-4 bg-white rounded-full transition-all", autoApprove ? "right-1" : "left-1")}></div>
                         </div>
                       </button>

                       <div className="space-y-2">
                         <div className="flex items-center gap-2 text-gray-400">
                           <Key size={14} />
                           <label className="text-[10px] font-bold uppercase tracking-[0.15em]">Join Password</label>
                         </div>
                         <input 
                           type="text"
                           value={joinPassword}
                           onChange={(e) => setJoinPassword(e.target.value)}
                           placeholder="Optional password"
                           className="w-full px-4 py-3 bg-white border-none rounded-xl focus:ring-2 focus:ring-brand-indigo/10 font-bold text-[10px] uppercase tracking-[0.15em] transition-all"
                         />
                       </div>
                     </motion.div>
                   )}
                </section>

                <section>
                   <h2 className="text-[10px] font-bold text-gray-400 uppercase tracking-[0.15em] mb-3">Community Rules</h2>
                   <div className="bg-gray-50 rounded-3xl p-5 space-y-3">
                      {rules.map((rule, i) => (
                        <div key={i} className="flex items-center justify-between p-3 bg-white rounded-xl shadow-sm border border-gray-100 group">
                           <div className="flex items-center gap-3">
                             <CheckCircle2 size={14} className="text-brand-indigo" />
                             <span className="font-bold text-xs text-gray-700 tracking-tight">{rule}</span>
                           </div>
                           <button onClick={() => removeRule(i)} className="text-gray-300 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-all">
                             <X size={14} />
                           </button>
                        </div>
                      ))}
                      
                      <div className="flex gap-2">
                        <input 
                          type="text"
                          value={newRule}
                          onChange={(e) => setNewRule(e.target.value)}
                          onKeyDown={(e) => e.key === 'Enter' && addRule()}
                          placeholder="Add a new rule..."
                          className="flex-1 bg-white border-none rounded-xl px-4 py-3 text-xs font-bold focus:ring-2 focus:ring-brand-indigo/10 outline-none"
                        />
                        <button 
                          onClick={addRule}
                          className="p-3 bg-brand-indigo text-white rounded-xl hover:bg-brand-indigo/90 transition-all shadow-lg shadow-brand-indigo/10"
                        >
                          <Plus size={18} />
                        </button>
                      </div>
                   </div>
                </section>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CommunitySettings;
